// Use environment variable for API URL, fallback to localhost for development
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export type TokenProvider = (options?: { skipCache?: boolean }) => Promise<string | null>;

type ApiError = Error & {
  status?: number;
  data?: unknown;
};

class AuthErrorEmitter {
  private listeners: Set<(error: Error) => void> = new Set();

  on(listener: (error: Error) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(error: Error): void {
    this.listeners.forEach((listener) => listener(error));
  }
}

export const authErrorEmitter = new AuthErrorEmitter();

type RequestOptions = RequestInit & {
  timeoutMs?: number;
};

export const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as { name?: unknown; message?: unknown };
  if (candidate.name === 'AbortError') {
    return true;
  }
  if (typeof candidate.message === 'string') {
    const normalized = candidate.message.toLowerCase();
    return normalized.includes('aborted');
  }
  return false;
};

export interface ReportMapInsideItem {
  promise: string;
  section_title?: string;
}

export interface ReportMapStop {
  id: string;
  kicker?: string;
  title: string;
  insight: string;
  inside?: ReportMapInsideItem[];
  section_title?: string;
  depth?: 'quick' | 'standard' | 'deep' | string;
  payoff?: 'Surprise' | 'Warning' | 'Opportunity' | 'Playbook' | 'Benchmark' | string;
  minutes?: number;
  tables?: number;
}

export interface ReportMapThread {
  label: string;
  question: string;
  stop_ids: string[];
}

export interface ReportMap {
  version?: number;
  map_title?: string;
  orientation?: string;
  reading_minutes?: number;
  stops: ReportMapStop[];
  threads?: ReportMapThread[];
  microreport_id?: string;
}

export interface MicroReport {
  id: string;
  title: string;
  date: string;
  content: string;
  type: string;
  map?: ReportMap | null;
}

export interface ChatMessage {
  role: string;
  content: string;
  timestamp: string;
}

export interface UserProfile {
  name: string | null;
  company: string | null;
  role: string | null;
}

export interface ChatRequest {
  message: string;
  session_id?: string;
  agent_id?: string;
  enable_web_search?: boolean;
}

export interface ChatResponse {
  message: string;
  session_id: string;
  timestamp: string;
}

export interface DocumentAssetInfo {
  doc_id: string;
  attachment_id?: string;
  filename: string;
  content_type?: string;
  status: 'queued' | 'parsing' | 'ready' | 'failed' | 'stuck' | string;
  error?: string | null;
  num_pages?: number | null;
  chunk_count?: number | null;
  created_at?: string | null;
}

export interface BriefBuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface BriefBuilderChatResponse {
  brief_id: string;
  reply: string;
  messages: BriefBuilderMessage[];
}

export interface ChatStreamOptions {
  onToken?: (chunk: string) => void;
}

export interface ExplorationModel {
  nick: string;
  label: string;
  provider: string;
  model: string;
  purpose: string;
  tier?: 'light' | 'medium' | 'premium';
}

export interface ExplorationSource {
  title: string;
  url: string;
}

export interface ExplorationChatResult {
  reply: string;
  sources: ExplorationSource[];
}

export interface ExplorationConversationSummary {
  session_id: string;
  title: string;
  updated_at: string | null;
  message_count: number;
  model_nick?: string | null;
}

export interface ExplorationStoredMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  mode?: 'chat' | 'search';
  model_nick?: string;
  model_label?: string;
  sources?: ExplorationSource[];
}

export interface GenerateReportRequest {
  goal: string;
  session_id?: string;
  agent_id?: string;
}

export interface GenerateReportResponse {
  report_id: string;
  title: string;
  content: string;
  session_id: string;
  timestamp: string;
}

export interface FrameCardPayload {
  frame_number: number;
  title: string;
  summary?: string;
  source?: string;
  details?: Record<string, unknown>;
}

export interface FrameCatalogResponse {
  selector?: FrameCardPayload[];
  generator?: FrameCardPayload[];
}

export interface SessionSummary {
  session_id: string;
  goal?: string;
  created_at?: string;
  stage?: string;
  report_count?: number;
  last_active_at?: string;
  session_name?: string | null;
  agent_id?: string | null;
  brief_id?: string | null;
}

export interface StepStatusItem {
  uid?: string;
  seq?: number;
  id?: number;
  microreport_id?: string;
  title?: string;
  description?: string;
  success_criteria?: string[];
  status?: string;
  report_id?: string;
  stream_log?: string;
  is_selected?: boolean;
}

export interface StepStatusResponse {
  steps: StepStatusItem[];
  stage?: string | null;
  session_name?: string | null;
  submitted_frames?: number[];
  completed_frames?: number[];
  reports_ready_to_generate?: boolean;
  augmentation_worker_running?: boolean;
  selected_microreport_ids?: string[];
  final_regenerating?: boolean;
}

export interface GenerationEstimateResponse {
  status: 'ready' | 'updating' | 'not_ready' | 'already_complete';
  pending_microreports: number;
  required_credits: number;
  cost_per_microreport: number;
  reports_ready_to_generate: boolean;
  augmentation_worker_running: boolean;
  stage: string;
  message?: string | null;
}

export interface LandingStatusStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'complete' | 'error';
  detail?: string;
  timestamp?: string | null;
}

export interface LandingStatusResponse {
  session_id?: string;
  steps: LandingStatusStep[];
  started_at?: string | null;
  updated_at?: string | null;
  ready?: boolean;
  status_text?: string;
  progress_percent?: number;
  checks?: {
    report_sections_ready?: boolean;
    framing_bundle_ready?: boolean;
    frames_payload_ready?: boolean;
    framing_artifacts_complete?: boolean;
  };
  blockers?: string[];
  launch_updated_at?: string | null;
  completed_artifacts?: number;
  total_artifacts?: number;
  artifact_status?: Record<string, boolean>;
}

export interface DevReportWorkerStartResponse {
  status: string;
  session_id: string;
  run_id: string;
  worker: {
    job_id: string;
    status: string;
    session_id: string;
    run_id: string;
    idempotency_key: string;
    created_at: string;
    updated_at: string;
  };
}

export interface DevReportWorkerJobResponse {
  job_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  session_id: string;
  run_id: string;
  idempotency_key: string;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  error_message?: string | null;
  result?: Record<string, unknown>;
}

export interface MicroReportSection {
  id?: string;
  name: string;
  purpose?: string;
  scope?: string;
  competitors?: string;
  output?: string;
}

export interface ReportSectionsResponse {
  microreports: MicroReportSection[];
  final_report: Record<string, unknown>;
  scope?: string;
  context?: string;
  competitors?: string;
  trend_categories?: string;
}

export interface LandingParseResponse {
  product?: string;
  market?: string;
  segments?: string;
  competitors?: string;
  goal?: string;
}

export interface LaunchFlowLogRequest {
  event_name: string;
  session_id?: string;
  timestamp?: string;
  [key: string]: unknown;
}

export interface CloseSessionResponse {
  status: string;
  mode: 'normal' | 'aborted' | 'detached';
  session_id: string;
  cleanup?: {
    removed_reports?: number;
    removed_files?: number;
    removed_worker_files?: number;
  };
}

export interface SessionInitResponse {
  session_id: string;
  created_at?: string;
  stage?: string | null;
  agent_id?: string | null;
}

class ApiService {
  private sessionId: string | null = null;
  private tokenProvider: TokenProvider | null = null;
  private creditsBalanceCache: { balance: number; fetchedAt: number } | null = null;
  private creditsBalanceInFlight: Promise<{ balance: number }> | null = null;
  private creditsBalanceTtlMs = 30000;

  private isNonCriticalAuthEndpoint(url: string): boolean {
    return url.includes('/credits/balance');
  }

  setTokenProvider(provider: TokenProvider | null) {
    const prevHadProvider = Boolean(this.tokenProvider);
    const nextHadProvider = Boolean(provider);
    this.tokenProvider = provider;
    if (prevHadProvider !== nextHadProvider) {
      this.creditsBalanceCache = null;
      this.creditsBalanceInFlight = null;
    }
  }

  private async withAuthHeaders(initHeaders?: HeadersInit, skipCache?: boolean): Promise<Headers> {
    const headers = new Headers(initHeaders || {});
    if (!headers.has('Authorization') && this.tokenProvider) {
      const token = await this.tokenProvider({ skipCache });
      if (!token) {
        throw new Error('Authentication token unavailable');
      }
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private async authenticatedRequest(
    url: string,
    init?: RequestOptions,
    retryCount: number = 0
  ): Promise<Response> {
    const shouldDebugLaunchUrl =
      url.includes('/landing-status') ||
      url.includes('/reports') ||
      url.includes('/frames') ||
      url.includes('/step-status') ||
      url.includes('/report-sections');
    const headers = await this.withAuthHeaders(init?.headers);
    const controller = new AbortController();
    const timeoutMs = typeof init?.timeoutMs === 'number' ? init.timeoutMs : this.defaultTimeoutMs(url);
    const timer: ReturnType<typeof setTimeout> | null = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

    const { timeoutMs: _timeoutMs, ...rest } = init || {};
    const nextInit: RequestInit = { ...rest, headers, signal: controller.signal };

    try {
      const response = await fetch(url, nextInit);

      if (response.status === 401 && retryCount === 0 && this.tokenProvider) {
        console.log('[Auth] Received 401, attempting retry with fresh token');
        const freshHeaders = await this.withAuthHeaders(init?.headers, true);
        const freshInit: RequestInit = { ...rest, headers: freshHeaders, signal: controller.signal };
        const retryResponse = await fetch(url, freshInit);
        if (!retryResponse.ok && shouldDebugLaunchUrl) {
          console.warn('[launch-debug] API non-2xx after auth retry', {
            url,
            status: retryResponse.status,
            method: freshInit.method || 'GET',
          });
        }

        if (
          retryResponse.status === 401 &&
          retryCount === 0 &&
          !this.isNonCriticalAuthEndpoint(url)
        ) {
          const error = new Error('Session expired') as ApiError;
          error.status = 401;
          authErrorEmitter.emit(error);
        }

        return retryResponse;
      }

      if (!response.ok && shouldDebugLaunchUrl) {
        console.warn('[launch-debug] API non-2xx', {
          url,
          status: response.status,
          method: nextInit.method || 'GET',
        });
      }

      return response;
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }
  }

  private defaultTimeoutMs(url: string): number {
    if (url.includes('/stream')) {
      return 0;
    }
    if (url.includes('/frame-selection')) {
      return 120000;
    }
    return 60000;
  }

  private async buildApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
    let data: unknown = null;
    let message = fallbackMessage;
    try {
      data = await response.clone().json();
      const payload = data as { detail?: unknown; message?: unknown } | null;
      if (typeof payload?.detail === 'string' && payload.detail.trim()) {
        message = payload.detail.trim();
      } else if (typeof payload?.message === 'string' && payload.message.trim()) {
        message = payload.message.trim();
      }
    } catch {
      try {
        const text = await response.text();
        if (text.trim()) {
          message = text.trim();
        }
      } catch {
      }
    }
    const error = new Error(message) as ApiError;
    error.status = response.status;
    error.data = data;
    return error;
  }

  private async request(url: string, init?: RequestOptions): Promise<Response> {
    return this.authenticatedRequest(url, init, 0);
  }

  private consumeSseBuffer(
    inputBuffer: string,
    onPayload: (payload: Record<string, unknown>) => void
  ): string {
    let buffer = inputBuffer.replace(/\r\n/g, '\n');
    let boundary = buffer.indexOf('\n\n');

    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 2);

      if (rawEvent) {
        const dataLines = rawEvent
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart());

        if (dataLines.length > 0) {
          const rawPayload = dataLines.join('\n').trim();
          if (rawPayload.length > 0) {
            let parsedPayload: unknown;
            try {
              parsedPayload = JSON.parse(rawPayload);
            } catch (err) {
              const reason = err instanceof Error ? err.message : String(err);
              throw new Error(`Invalid SSE payload: ${reason}`);
            }
            if (!parsedPayload || typeof parsedPayload !== 'object') {
              throw new Error('Invalid SSE payload shape');
            }
            onPayload(parsedPayload as Record<string, unknown>);
          }
        }
      }

      boundary = buffer.indexOf('\n\n');
    }

    return buffer;
  }

  private async readSsePayloads(
    response: Response,
    onPayload: (payload: Record<string, unknown>) => void,
    isComplete: () => boolean
  ): Promise<void> {
    if (!response.body) {
      throw new Error('Streaming is not supported in this browser.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      buffer = this.consumeSseBuffer(buffer, onPayload);
      if (isComplete()) {
        try {
          await reader.cancel();
        } catch {
        }
        break;
      }
    }

    if (!isComplete()) {
      buffer += decoder.decode();
      buffer = this.consumeSseBuffer(buffer, onPayload);
    }
  }

  async getMicroReports(): Promise<MicroReport[]> {
    const response = await this.request(`${API_BASE_URL}/microreports`);
    if (!response.ok) {
      throw new Error('Failed to fetch microanalyses');
    }
    return response.json();
  }

  async getMicroReport(reportId: string): Promise<MicroReport> {
    const response = await this.request(`${API_BASE_URL}/microreports/${reportId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch microanalysis');
    }
    return response.json();
  }

  async sendChatMessage(message: string, agentId?: string): Promise<ChatResponse> {
    const request: ChatRequest = {
      message,
      session_id: this.sessionId || undefined,
      agent_id: agentId,
    };

    const response = await this.request(`${API_BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to send chat message');
    }

    const result = await response.json();
    this.sessionId = result.session_id;
    return result;
  }

  async streamChatMessage(message: string, onToken?: (chunk: string) => void, agentId?: string): Promise<ChatResponse> {
    const request: ChatRequest = {
      message,
      session_id: this.sessionId || undefined,
      agent_id: agentId,
    };

    let response: Response | null = null;
    try {
      response = await this.request(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (response.status === 404) {
        return this.sendChatMessage(message, agentId);
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Streaming chat failed');
      }

      let finalMessage = '';
      let sessionId = this.sessionId;
      let timestamp = new Date().toISOString();
      let streamComplete = false;

      await this.readSsePayloads(
        response,
        (payload) => {
          const type = typeof payload.type === 'string' ? payload.type : '';
          if (typeof payload.session_id === 'string' && payload.session_id) {
            sessionId = payload.session_id;
          }
          if (type === 'token' && typeof payload.token === 'string') {
            finalMessage += payload.token;
            onToken?.(payload.token);
            return;
          }
          if (type === 'complete') {
            if (typeof payload.message === 'string') {
              finalMessage = payload.message;
            }
            if (typeof payload.timestamp === 'string' && payload.timestamp) {
              timestamp = payload.timestamp;
            }
            streamComplete = true;
            return;
          }
          if (type === 'error') {
            const errorMessage = typeof payload.error === 'string' ? payload.error : 'Streaming error';
            throw new Error(errorMessage);
          }
        },
        () => streamComplete
      );

      if (!streamComplete) {
        throw new Error('Streaming chat finished without a completion event');
      }

      const resolvedSessionId = sessionId || this.sessionId || '';
      this.sessionId = resolvedSessionId;

      return {
        message: finalMessage,
        session_id: resolvedSessionId,
        timestamp,
      };
    } catch (err) {
      if (response && !response.ok && response.status === 404) {
        return this.sendChatMessage(message, agentId);
      }
      throw err;
    }
  }

  async strategyChat(message: string, agentId?: string): Promise<ChatResponse> {
    const request: ChatRequest = {
      message,
      session_id: this.sessionId || undefined,
      agent_id: agentId,
    };
    const response = await this.request(`${API_BASE_URL}/strategy-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error('Strategy chat failed');
    }
    const result = await response.json();
    this.sessionId = result.session_id;
    return result;
  }

  async sendBriefBuilderMessage(
    message: string,
    options?: { briefId?: string; sessionId?: string; agentId?: string }
  ): Promise<BriefBuilderChatResponse> {
    const payload: Record<string, unknown> = {
      message,
      brief_id: options?.briefId,
      session_id: options?.sessionId,
      agent_id: options?.agentId,
    };

    let response = await this.request(`${API_BASE_URL}/brief-builder/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    // If the backend no longer recognizes this brief session (e.g., routed to another instance),
    // retry once without brief_id so the server can create/recover a fresh brief session.
    if (response.status === 404 && options?.briefId) {
      const retryPayload: Record<string, unknown> = {
        message,
        session_id: options?.sessionId,
        agent_id: options?.agentId,
      };
      response = await this.request(`${API_BASE_URL}/brief-builder/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(retryPayload),
      });
    }
    if (!response.ok) {
      throw new Error('Failed to contact the brief builder');
    }
    return response.json();
  }

  async streamBriefBuilderMessage(
    message: string,
    options?: { briefId?: string; sessionId?: string; agentId?: string; onToken?: (chunk: string) => void }
  ): Promise<BriefBuilderChatResponse> {
    const makePayload = (briefId?: string): Record<string, unknown> => ({
      message,
      brief_id: briefId,
      session_id: options?.sessionId,
      agent_id: options?.agentId,
    });

    let response = await this.request(`${API_BASE_URL}/brief-builder/message/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(makePayload(options?.briefId)),
    });

    // Recover from stale brief session IDs by retrying stream once without brief_id.
    if (response.status === 404 && options?.briefId) {
      response = await this.request(`${API_BASE_URL}/brief-builder/message/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(makePayload(undefined)),
      });
    }

    if (response.status === 404) {
      return this.sendBriefBuilderMessage(message, {
        briefId: undefined,
        sessionId: options?.sessionId,
        agentId: options?.agentId,
      });
    }

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to contact the brief builder');
    }

    let resolvedBriefId = options?.briefId || '';
    let finalMessages: BriefBuilderMessage[] | null = null;
    let streamComplete = false;

    await this.readSsePayloads(
      response,
      (payload) => {
        const type = typeof payload.type === 'string' ? payload.type : '';
        if (typeof payload.brief_id === 'string' && payload.brief_id) {
          resolvedBriefId = payload.brief_id;
        }
        if (type === 'token' && typeof payload.token === 'string') {
          options?.onToken?.(payload.token);
          return;
        }
        if (type === 'complete') {
          if (typeof payload.brief_id === 'string' && payload.brief_id) {
            resolvedBriefId = payload.brief_id;
          }
          if (Array.isArray(payload.messages)) {
            finalMessages = payload.messages as BriefBuilderMessage[];
          }
          streamComplete = true;
          return;
        }
        if (type === 'error') {
          const errorMessage = typeof payload.error === 'string' ? payload.error : 'Streaming error';
          throw new Error(errorMessage);
        }
      },
      () => streamComplete
    );

    if (!finalMessages) {
      throw new Error('Streaming finished without a completion event');
    }

    const normalizedMessages: BriefBuilderMessage[] = finalMessages;
    const reply =
      normalizedMessages.length > 0
        ? normalizedMessages[normalizedMessages.length - 1].content
        : '';

    return {
      brief_id: resolvedBriefId,
      messages: normalizedMessages,
      reply,
    };
  }

  async fetchExplorationModels(): Promise<ExplorationModel[]> {
    const response = await this.request(`${API_BASE_URL}/exploration/models`);
    if (!response.ok) {
      throw new Error('Failed to load exploration models');
    }
    const data = await response.json();
    return Array.isArray(data.models) ? (data.models as ExplorationModel[]) : [];
  }

  async listExplorationConversations(): Promise<ExplorationConversationSummary[]> {
    const response = await this.request(`${API_BASE_URL}/exploration/conversations`);
    if (!response.ok) {
      throw new Error('Failed to load conversations');
    }
    const data = await response.json();
    return Array.isArray(data.conversations)
      ? (data.conversations as ExplorationConversationSummary[])
      : [];
  }

  async getExplorationConversation(sessionId: string): Promise<{
    session_id: string;
    title: string;
    messages: ExplorationStoredMessage[];
    model_nick?: string | null;
  }> {
    const response = await this.request(
      `${API_BASE_URL}/exploration/conversations/${sessionId}`
    );
    if (!response.ok) {
      throw new Error('Conversation not found');
    }
    return response.json();
  }

  async deleteExplorationConversation(sessionId: string): Promise<void> {
    const response = await this.request(
      `${API_BASE_URL}/exploration/conversations/${sessionId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      throw new Error('Failed to delete conversation');
    }
  }

  async streamExplorationMessage(
    message: string,
    options: {
      modelNick: string;
      mode: 'chat' | 'search';
      history: { role: string; content: string }[];
      sessionId?: string | null;
      onToken?: (chunk: string) => void;
    }
  ): Promise<ExplorationChatResult> {
    const response = await this.request(`${API_BASE_URL}/exploration/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        model_nick: options.modelNick,
        mode: options.mode,
        history: options.history,
        session_id: options.sessionId || undefined,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      const error = new Error(text || 'Exploration chat failed') as ApiError;
      error.status = response.status;
      throw error;
    }

    let reply = '';
    let sources: ExplorationSource[] = [];
    let streamComplete = false;

    await this.readSsePayloads(
      response,
      (payload) => {
        const type = typeof payload.type === 'string' ? payload.type : '';
        if (type === 'token' && typeof payload.token === 'string') {
          options.onToken?.(payload.token);
          return;
        }
        if (type === 'complete') {
          if (typeof payload.reply === 'string') {
            reply = payload.reply;
          }
          if (Array.isArray(payload.sources)) {
            sources = payload.sources as ExplorationSource[];
          }
          streamComplete = true;
          return;
        }
        if (type === 'error') {
          const errorMessage = typeof payload.error === 'string' ? payload.error : 'Streaming error';
          throw new Error(errorMessage);
        }
      },
      () => streamComplete
    );

    return { reply, sources };
  }

  async fetchBriefBuilderSession(briefId: string): Promise<{ brief_id: string; messages: BriefBuilderMessage[]; final_prompt?: string }> {
    const response = await this.request(`${API_BASE_URL}/brief-builder/${briefId}`);
    if (!response.ok) {
      throw new Error('Brief builder session not found');
    }
    return response.json();
  }

  async initiateAttachment(
    filename: string,
    contentType: string,
    sessionId?: string | null
  ): Promise<{ attachment_id: string; upload_url: string }> {
    const response = await this.request(`${API_BASE_URL}/attachments/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        content_type: contentType,
        session_id: sessionId || undefined,
      }),
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to start upload');
    }
    return response.json();
  }

  uploadFileToSignedUrl(
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    // XMLHttpRequest instead of fetch so we can report upload progress.
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed (network error)'));
      xhr.send(file);
    });
  }

  async completeAttachment(
    attachmentId: string,
    options?: { sizeBytes?: number; briefId?: string | null }
  ): Promise<{ attachment_id: string; status: string; doc_id?: string | null; ingestion?: string | null }> {
    const response = await this.request(`${API_BASE_URL}/attachments/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attachment_id: attachmentId,
        size_bytes: options?.sizeBytes,
        brief_id: options?.briefId || undefined,
      }),
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to finish upload');
    }
    return response.json();
  }

  async listDocuments(options?: {
    briefId?: string | null;
    sessionId?: string | null;
  }): Promise<{ documents: DocumentAssetInfo[] }> {
    const params = new URLSearchParams();
    if (options?.briefId) params.set('brief_id', options.briefId);
    if (options?.sessionId) params.set('session_id', options.sessionId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.request(`${API_BASE_URL}/documents${suffix}`);
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to list documents');
    }
    return response.json();
  }

  async getDocumentStatus(docId: string): Promise<DocumentAssetInfo> {
    const response = await this.request(`${API_BASE_URL}/documents/${docId}/status`);
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to get document status');
    }
    return response.json();
  }

  async retryDocument(docId: string): Promise<{ doc_id: string; status: string }> {
    const response = await this.request(`${API_BASE_URL}/documents/${docId}/retry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to retry document');
    }
    return response.json();
  }

  async deleteDocument(docId: string): Promise<{ doc_id: string; deleted: boolean }> {
    const response = await this.request(`${API_BASE_URL}/documents/${docId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to delete document');
    }
    return response.json();
  }

  async clearChatHistory(): Promise<{ status: string; message: string; session_id: string }> {
    if (!this.sessionId) {
      throw new Error('No session ID available');
    }
    const response = await this.request(`${API_BASE_URL}/strategy-chat/history/${this.sessionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to clear chat history');
    }
    return response.json();
  }

  async streamStrategyChat(message: string, onToken?: (chunk: string) => void, agentId?: string, enableWebSearch?: boolean): Promise<ChatResponse> {
    const request: ChatRequest = {
      message,
      session_id: this.sessionId || undefined,
      agent_id: agentId,
      enable_web_search: enableWebSearch,
    };

    let response: Response | null = null;
    try {
      response = await this.request(`${API_BASE_URL}/strategy-chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (response.status === 404) {
        return this.strategyChat(message);
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Streaming chat failed');
      }

      let finalMessage = '';
      let sessionId = this.sessionId;
      let timestamp = new Date().toISOString();
      let streamComplete = false;

      await this.readSsePayloads(
        response,
        (payload) => {
          const type = typeof payload.type === 'string' ? payload.type : '';
          if (typeof payload.session_id === 'string' && payload.session_id) {
            sessionId = payload.session_id;
          }
          if (type === 'token' && typeof payload.token === 'string') {
            finalMessage += payload.token;
            onToken?.(payload.token);
            return;
          }
          if (type === 'complete') {
            if (typeof payload.message === 'string') {
              finalMessage = payload.message;
            }
            if (typeof payload.timestamp === 'string' && payload.timestamp) {
              timestamp = payload.timestamp;
            }
            streamComplete = true;
            return;
          }
          if (type === 'error') {
            const errorMessage = typeof payload.error === 'string' ? payload.error : 'Streaming error';
            throw new Error(errorMessage);
          }
        },
        () => streamComplete
      );

      if (!streamComplete) {
        throw new Error('Streaming strategy chat finished without a completion event');
      }

      const resolvedSessionId = sessionId || this.sessionId || '';
      this.sessionId = resolvedSessionId;

      return {
        message: finalMessage,
        session_id: resolvedSessionId,
        timestamp,
      };
    } catch (err) {
      if (response && !response.ok && response.status === 404) {
        return this.strategyChat(message);
      }
      throw err;
    }
  }

  async generateFramingReport(goal: string, agentId?: string, sessionId?: string): Promise<GenerateReportResponse> {
    const request: GenerateReportRequest = {
      goal,
      session_id: sessionId || this.sessionId || undefined,
      agent_id: agentId,
    };

    const response = await this.request(`${API_BASE_URL}/generate-framing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      timeoutMs: 300000,
    });

    if (!response.ok) {
      throw new Error('Failed to generate framing analysis');
    }

    const result = await response.json();
    this.sessionId = result.session_id;
    return result;
  }

  async launchWorkspaceFromBrief(briefId: string, finalPrompt: string, agentId?: string, sessionId?: string): Promise<GenerateReportResponse> {
    const response = await this.request(`${API_BASE_URL}/brief-builder/${briefId}/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        final_prompt: finalPrompt,
        session_id: sessionId,
        agent_id: agentId,
      }),
      timeoutMs: 300000,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to launch workspace');
    }
    const payload: GenerateReportResponse = await response.json();
    this.sessionId = payload.session_id;
    return payload;
  }

  async submitFrameSelection(frames: number[], notes?: string, autoRun?: boolean): Promise<ChatResponse> {
    if (!this.sessionId) {
      throw new Error('No active session. Generate a framing analysis first.');
    }

    const response = await this.request(`${API_BASE_URL}/frame-selection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: this.sessionId,
        frames,
        notes,
        auto_run: !!autoRun,
      }),
      timeoutMs: 120000,
    });

    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to submit frame selection');
    }

    const result = await response.json();
    this.sessionId = result.session_id;
    return result;
  }

  async generateSingleMicroreport(microreportIndex: number): Promise<{ status: string; message: string; microreport_index: number; microreport_name: string; credits_consumed: number }> {
    if (!this.sessionId) {
      throw new Error('No active session. Generate a framing analysis first.');
    }

    const response = await this.request(`${API_BASE_URL}/sessions/${this.sessionId}/generate-microreport/${microreportIndex}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail?.message || errorData.detail || `Failed to generate microanalysis ${microreportIndex}`);
    }

    return response.json();
  }

  async getSessionMessages(sessionId: string): Promise<ChatMessage[]> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/messages`);
    if (!response.ok) {
      throw new Error('Failed to fetch session messages');
    }
    return response.json();
  }

  async getSessionReports(sessionId: string): Promise<MicroReport[]> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/reports`);
    if (!response.ok) {
      throw new Error('Failed to fetch session analyses');
    }
    return response.json();
  }

  async getSessionFrames(sessionId: string): Promise<FrameCatalogResponse> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/frames`);
    if (!response.ok) {
      throw new Error('Failed to fetch session frames');
    }
    return response.json();
  }

  async getStepStatus(sessionId: string): Promise<StepStatusResponse> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/step-status`);
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to fetch step status');
    }
    return response.json();
  }

  async startResearchWorker(sessionId: string): Promise<{ status: string; message: string }> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/start-research`, {
      method: 'POST',
      timeoutMs: 90000,
    });
    if (!response.ok) {
      let payload: any = null;
      try {
        payload = await response.json();
      } catch (_jsonError) {
        payload = null;
      }
      const detail = payload?.detail;
      let message = 'Failed to start research worker';
      if (typeof detail === 'string' && detail.trim()) {
        message = detail.trim();
      } else if (typeof payload?.message === 'string' && payload.message.trim()) {
        message = payload.message.trim();
      }
      const error = new Error(message) as Error & { status?: number; data?: any };
      error.status = response.status;
      error.data = payload;
      throw error;
    }
    return response.json();
  }

  async killGeneration(sessionId: string): Promise<{ status: string; session_id: string; cancelled: boolean; auto_cancelled?: boolean }> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/kill-generation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      timeoutMs: 15000,
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to stop generation');
    }
    return response.json();
  }

  async getGenerationEstimate(sessionId: string, selectedFrameNumbers?: number[]): Promise<GenerationEstimateResponse> {
    const params = new URLSearchParams();
    if (Array.isArray(selectedFrameNumbers) && selectedFrameNumbers.length > 0) {
      const normalized = Array.from(
        new Set(
          selectedFrameNumbers
            .filter((value) => Number.isFinite(value))
            .map((value) => Math.trunc(value))
            .filter((value) => value > 0)
        )
      ).sort((a, b) => a - b);
      if (normalized.length > 0) {
        params.set('selected_frames', normalized.join(','));
      }
    }
    const query = params.toString();
    const url = `${API_BASE_URL}/sessions/${sessionId}/generation-estimate${query ? `?${query}` : ''}`;
    const response = await this.request(url);
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to fetch generation estimate');
    }
    return response.json();
  }

  async skipFrameSelection(sessionId: string): Promise<{ status: string; message: string }> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/skip-frame-selection`, {
      method: 'POST',
      timeoutMs: 300000,
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to skip frame selection');
    }
    return response.json();
  }

  async getLandingStatus(sessionId: string): Promise<LandingStatusResponse | null> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/landing-status`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch landing status');
    }
    return response.json();
  }

  async startDevReportWorkerSmokeTest(sessionId?: string): Promise<DevReportWorkerStartResponse> {
    const response = await this.request(`${API_BASE_URL}/dev/report-worker/smoke/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId || undefined }),
      timeoutMs: 45000,
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to start report worker smoke test');
    }
    return response.json();
  }

  async getDevReportWorkerSmokeStatus(jobId: string): Promise<DevReportWorkerJobResponse> {
    const response = await this.request(`${API_BASE_URL}/dev/report-worker/smoke/${jobId}`, {
      timeoutMs: 30000,
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to fetch report worker smoke status');
    }
    return response.json();
  }

  async getReportSections(sessionId: string): Promise<ReportSectionsResponse | null> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/report-sections`);
    if (response.status === 404) {
      console.log('[API] Report sections not found (404) for session:', sessionId);
      return null;
    }
    if (!response.ok) {
      // Return null if file doesn't exist yet (not an error)
      if (response.status === 500) {
        console.log('[API] Report sections error (500) for session:', sessionId);
        return null;
      }
      throw await this.buildApiError(response, 'Failed to fetch report sections');
    }
    const data = await response.json();
    console.log('[API] Report sections response:', data);
    return data;
  }

  async parseLandingPrompt(prompt: string): Promise<LandingParseResponse> {
    const response = await this.request(`${API_BASE_URL}/landing-parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      throw new Error('Failed to parse prompt');
    }

    return response.json();
  }

  async logLaunchFlow(payload: LaunchFlowLogRequest): Promise<void> {
    try {
      await this.request(`${API_BASE_URL}/log-launch-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: 8000,
      });
    } catch {
      // Telemetry must never interrupt user flow.
    }
  }

  async saveReportExport(reportId: string, markdown: string, options?: { title?: string; subtitle?: string }) {
    const response = await this.request(`${API_BASE_URL}/reports/${reportId}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        markdown,
        title: options?.title,
        subtitle: options?.subtitle,
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to save analysis');
    }

    return response.json();
  }

  async exportReportDocx(reportId: string, markdown?: string, options?: { title?: string; subtitle?: string; is_custom?: boolean }): Promise<Blob> {
    const response = await this.request(`${API_BASE_URL}/reports/${reportId}/docx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        markdown,
        title: options?.title,
        subtitle: options?.subtitle,
        is_custom: options?.is_custom ?? false,
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to export analysis');
    }

    return response.blob();
  }

  async exportReportPdf(reportId: string, html: string, options?: { title?: string; is_custom?: boolean }): Promise<Blob> {
    const response = await this.request(`${API_BASE_URL}/reports/${reportId}/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, title: options?.title, is_custom: options?.is_custom ?? false }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to export PDF');
    }

    return response.blob();
  }

  async listSessions(): Promise<SessionSummary[]> {
    const response = await this.request(`${API_BASE_URL}/sessions`);
    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }
    return response.json();
  }

  async initializeSession(options?: { source?: string; metadata?: Record<string, unknown>; agentId?: string }): Promise<SessionInitResponse> {
    const payload: Record<string, unknown> = {
      source: options?.source,
      metadata: options?.metadata,
      agent_id: options?.agentId,
    };
    const response = await this.request(`${API_BASE_URL}/sessions/initialize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to initialize session');
    }
    return response.json();
  }

  async abandonSession(sessionId: string): Promise<{ status: string; session_id: string }> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}/abandon`, {
      method: 'POST',
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to abandon session');
    }
    return response.json();
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  setSessionId(nextSessionId: string | null) {
    this.sessionId = nextSessionId;
  }

  async setSessionName(name: string): Promise<{ session_name: string }> {
    if (!this.sessionId) {
      throw new Error('No active session. Generate or resume a workspace first.');
    }
    const response = await this.request(`${API_BASE_URL}/sessions/${this.sessionId}/name`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Failed to save session name');
    }
    return response.json();
  }

  async deleteSession(sessionId: string): Promise<{ status: string; session_id: string }> {
    const response = await this.request(`${API_BASE_URL}/sessions/${sessionId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to delete session');
    }
    if (this.sessionId === sessionId) {
      this.sessionId = null;
    }
    return response.json();
  }

  async closeSession(reason?: string): Promise<CloseSessionResponse> {
    if (!this.sessionId) {
      return { status: 'noop', mode: 'normal', session_id: '' };
    }
    const response = await this.request(`${API_BASE_URL}/sessions/${this.sessionId}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to close workspace');
    }
    const payload: CloseSessionResponse = await response.json();
    this.sessionId = null;
    return payload;
  }

  async saveChatMessage(role: string, content: string, sessionId?: string, agentId?: string): Promise<ServerChatMessage> {
    const response = await this.request(`${API_BASE_URL}/chat-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, content, session_id: sessionId, agent_id: agentId }),
      timeoutMs: 10000,
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to save chat message');
    }
    return response.json();
  }

  async getChatMessages(sessionId?: string): Promise<ServerChatMessage[]> {
    const url = sessionId 
      ? `${API_BASE_URL}/chat-messages?session_id=${sessionId}`
      : `${API_BASE_URL}/chat-messages`;
    const response = await this.request(url);
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to fetch chat messages');
    }
    const data: ChatMessagesListResponse = await response.json();
    return data.messages;
  }

  // Credit System Methods
  async getCreditsBalance(): Promise<{ balance: number }> {
    const now = Date.now();
    if (this.creditsBalanceCache && now - this.creditsBalanceCache.fetchedAt < this.creditsBalanceTtlMs) {
      return { balance: this.creditsBalanceCache.balance };
    }
    if (this.creditsBalanceInFlight) {
      return this.creditsBalanceInFlight;
    }

    const requestPromise = (async () => {
      const response = await this.request(`${API_BASE_URL}/credits/balance`);
      if (!response.ok) {
        if ((response.status === 401 || response.status === 403) && this.creditsBalanceCache) {
          // Keep workflow UX stable when a transient auth blip hits the credits widget.
          return { balance: this.creditsBalanceCache.balance };
        }
        throw await this.buildApiError(response, 'Failed to fetch credit balance');
      }
      const payload = await response.json();
      const balance = Number.isFinite(payload?.balance) ? payload.balance : 0;
      this.creditsBalanceCache = { balance, fetchedAt: Date.now() };
      return { balance };
    })();

    this.creditsBalanceInFlight = requestPromise;
    try {
      return await requestPromise;
    } finally {
      if (this.creditsBalanceInFlight === requestPromise) {
        this.creditsBalanceInFlight = null;
      }
    }
  }

  invalidateCreditsCache(): void {
    this.creditsBalanceCache = null;
    this.creditsBalanceInFlight = null;
  }

  async getCreditsTransactions(): Promise<Array<{
    id: string;
    amount: number;
    type: string;
    description: string | null;
    created_at: string;
  }>> {
    const response = await this.request(`${API_BASE_URL}/credits/transactions`);
    if (!response.ok) {
      throw new Error('Failed to fetch credit transactions');
    }
    return response.json();
  }

  async consumeCredits(
    amount: number,
    description?: string,
    referenceId?: string
  ): Promise<{ success: boolean; consumed: number; remaining: number }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const response = await this.request(`${API_BASE_URL}/credits/consume`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        amount,
        description,
        reference_id: referenceId
      })
    });
    
    if (response.status === 402) {
      const error = await response.json();
      throw new Error(error.detail?.message || 'Insufficient credits');
    }
    
    if (!response.ok) {
      throw new Error('Failed to consume credits');
    }

    const payload = await response.json();
    if (payload && typeof payload.remaining === 'number' && Number.isFinite(payload.remaining)) {
      this.creditsBalanceCache = { balance: payload.remaining, fetchedAt: Date.now() };
    }
    return payload;
  }

  async getAgentAccess(): Promise<{
    competitive: boolean;
    market_research: boolean;
    customer_research: boolean;
  }> {
    const response = await this.request(`${API_BASE_URL}/agents/access`);
    if (!response.ok) {
      throw new Error('Failed to fetch agent access');
    }
    return response.json();
  }

  // User Profile Methods
  async getUserProfile(): Promise<UserProfile> {
    const response = await this.request(`${API_BASE_URL}/users/profile`);
    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }
    return response.json();
  }

  async updateUserProfile(profile: { name?: string; company?: string; role?: string }): Promise<UserProfile> {
    const response = await this.request(`${API_BASE_URL}/users/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile),
    });
    if (!response.ok) {
      throw new Error('Failed to update user profile');
    }
    return response.json();
  }

  async adminLogin(password: string): Promise<{ success: boolean; message: string }> {
    const response = await this.request(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Not authorized as admin');
      }
      throw new Error('Login failed');
    }
    return response.json();
  }

  async adminVerify(): Promise<{ is_admin: boolean; is_authenticated: boolean; email: string }> {
    const response = await this.request(`${API_BASE_URL}/admin/verify`);
    if (!response.ok) {
      throw new Error('Failed to verify admin status');
    }
    return response.json();
  }

  async adminListUsers(password: string, email?: string): Promise<{
    users: Array<{
      id: string;
      email: string | null;
      clerk_user_id: string | null;
      credits: number;
      agent_access: {
        competitive: boolean;
        market_research: boolean;
        customer_research: boolean;
      };
      created_at: string | null;
    }>;
    total: number;
  }> {
    const url = new URL(`${API_BASE_URL}/admin/users`);
    if (email) {
      url.searchParams.set('email', email);
    }
    const response = await this.request(url.toString(), {
      headers: { 'X-Admin-Password': password },
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Admin not configured');
      }
      throw new Error('Failed to fetch users');
    }
    return response.json();
  }

  async adminGetUser(userId: string): Promise<{
    id: string;
    email: string | null;
    clerk_user_id: string | null;
    credits: number;
    agent_access: {
      competitive: boolean;
      market_research: boolean;
      customer_research: boolean;
    };
    created_at: string | null;
  }> {
    const response = await this.request(`${API_BASE_URL}/admin/users/${userId}`);
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      if (response.status === 404) {
        throw new Error('User not found');
      }
      throw new Error('Failed to fetch user');
    }
    return response.json();
  }

  async adminAddCredits(password: string, userId: string, amount: number, description?: string): Promise<{
    user_id: string;
    new_balance: number;
    added: number;
  }> {
    const response = await this.request(`${API_BASE_URL}/admin/users/${userId}/credits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Password': password
      },
      body: JSON.stringify({ amount, description }),
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Admin not configured');
      }
      if (response.status === 404) {
        throw new Error('User not found');
      }
      throw new Error('Failed to update credits');
    }
    return response.json();
  }

  async adminUpdatePermissions(password: string, userId: string, permissions: {
    competitive?: boolean;
    market_research?: boolean;
    customer_research?: boolean;
  }): Promise<{
    user_id: string;
    permissions: {
      competitive: boolean;
      market_research: boolean;
      customer_research: boolean;
    };
  }> {
    const response = await this.request(`${API_BASE_URL}/admin/users/${userId}/permissions`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Password': password
      },
      body: JSON.stringify(permissions),
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Admin not configured');
      }
      if (response.status === 404) {
        throw new Error('User not found');
      }
      throw new Error('Failed to update permissions');
    }
    return response.json();
  }

}

export interface ServerChatMessage {
  id: string;
  role: string;
  content: string;
  session_id?: string;
  agent_id?: string;
  created_at: string;
}

export interface ChatMessagesListResponse {
  messages: ServerChatMessage[];
}

export const apiService = new ApiService();
